/**
 * Client-side URL utilities for subdomain detection and redirects.
 * Mirrors the logic from middleware.ts for use in client components.
 */

/**
 * Extract subdomain from hostname (client-side version)
 * Examples:
 * - casais.bite-club.app -> 'casais'
 * - tech.localhost -> 'tech'
 * - localhost -> null
 * - bite-club.app -> null
 */
export function extractSubdomainClient(hostname: string): string | null {
  // Remove port if present
  const host = hostname.split(':')[0];
  const parts = host.split('.');

  // For localhost development (tech.localhost)
  if (host.includes('.localhost')) {
    const subdomain = parts[0];
    return subdomain === 'localhost' ? null : subdomain;
  }

  // Ignore Cloudflare tunnel domains
  if (host.includes('.trycloudflare.com')) {
    return null;
  }

  // For production domains (casais.bite-club.app)
  // Need at least 3 parts: subdomain.domain.tld
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Ignore www, staging, dev subdomains
    if (['www', 'staging', 'dev'].includes(subdomain)) {
      return null;
    }
    return subdomain;
  }

  return null;
}

/**
 * Get the root domain URL (without subdomain)
 * Examples:
 * - casais.bite-club.app -> bite-club.app
 * - tech.localhost:3000 -> localhost:3000
 * - localhost:3000 -> localhost:3000
 * - bite-club.app -> bite-club.app
 */
export function getRootDomainUrl(): string {
  const { protocol, hostname, port } = window.location;
  const subdomain = extractSubdomainClient(hostname);

  if (!subdomain) {
    // Already on root domain, return as-is
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }

  // Strip the subdomain
  const host = hostname.split(':')[0];
  const parts = host.split('.');

  let rootHost: string;

  if (host.includes('.localhost')) {
    // tech.localhost -> localhost
    rootHost = 'localhost';
  } else {
    // casais.bite-club.app -> bite-club.app
    rootHost = parts.slice(1).join('.');
  }

  return `${protocol}//${rootHost}${port ? `:${port}` : ''}`;
}

/**
 * Get the correct app redirect URL based on current domain context.
 * - If on a subdomain: redirects to root domain's /app
 * - If already on root domain: redirects to /app
 */
export function getAppRedirectUrl(queryParams?: string): string {
  const subdomain = extractSubdomainClient(window.location.hostname);
  const query = queryParams ? `?${queryParams}` : '';

  if (subdomain) {
    // On a subdomain, redirect to root domain
    return `${getRootDomainUrl()}/app${query}`;
  }

  // Already on root domain, just go to /app
  return `/app${query}`;
}

/**
 * Redirect to the app with proper URL handling.
 * Use this instead of directly setting window.location.href.
 */
export function redirectToApp(queryParams?: string): void {
  window.location.href = getAppRedirectUrl(queryParams);
}

/**
 * Hard-navigate to a login page after signing out.
 *
 * Hard, and not `router.replace`: the browser must drop the whole JS heap —
 * including the module-scope TanStack QueryClient in `src/providers/query-provider.tsx`
 * — and re-request the document through the proxy with the cleared session cookies.
 * A soft navigation keeps the previous user's cached rows alive in the tab.
 *
 * `replace`, not `href`: assigning `href` keeps the signed-out document as the Back
 * target, and a browser with a back-forward cache restores such a document frozen,
 * without asking the proxy, rows and all. Replacing the history entry removes that
 * target; AuthCacheSync covers any older /app entry restored the same way.
 *
 * Use this instead of directly setting window.location.
 */
export function redirectToLogin(path: string): void {
  window.location.replace(path);
}

/**
 * Reload the current document.
 *
 * Used when the identity behind this document changes: clearing the query cache is not
 * enough, because mounted observers keep rendering their last snapshot and
 * AppUserContext is server-rendered and so still holds the previous user. Only a fresh
 * document re-runs AppUserServerProvider.
 *
 * Use this instead of directly calling window.location.reload().
 */
export function reloadPage(): void {
  window.location.reload();
}
